import type { GraphNode, GraphVisualizationFrame } from "@salesko/contracts";
import type { Dictionary } from "../../i18n";
import { entitySingularLabel } from "../i18n";

export type DetailAbout = {
  kind: "about" | "evidence";
  text: string;
};

export function initialsOf(label: string) {
  const parts = label.trim().split(/\s+/).filter(Boolean);
  const first = parts[0] ?? "";
  if (!first) {
    return "?";
  }
  if (parts.length === 1) {
    return first.slice(0, 2).toUpperCase();
  }
  const last = parts[parts.length - 1] ?? "";
  return (first.charAt(0) + last.charAt(0)).toUpperCase();
}

export function detailSubtitle(node: GraphNode, frame: GraphVisualizationFrame | null, t: Dictionary) {
  if (frame?.ui.detailPanel.selectedNodeId === node.id && frame.ui.detailPanel.subtitle) {
    return frame.ui.detailPanel.subtitle;
  }

  if (typeof node.data.role === "string") {
    return node.data.role;
  }
  if (typeof node.data.source === "string") {
    return `${entitySingularLabel(t, node.type)} · ${node.data.source}`;
  }

  return entitySingularLabel(t, node.type);
}

// A bio and a provenance line are different claims, so the caller can label them
// differently instead of showing "Google Calendar · ... · 2022-12-20" under "About".
// No synthesized filler: a node with neither gets null and the caller renders its
// own empty state.
export function detailAbout(
  node: GraphNode,
  frame: GraphVisualizationFrame | null
): DetailAbout | null {
  if (frame?.ui.detailPanel.selectedNodeId === node.id && frame.ui.detailPanel.about) {
    return { kind: "about", text: frame.ui.detailPanel.about };
  }

  if (node.summary) {
    return { kind: "about", text: node.summary };
  }
  if (node.evidence) {
    return { kind: "evidence", text: node.evidence };
  }

  return null;
}

export function detailTags(node: GraphNode, frame: GraphVisualizationFrame | null) {
  const source =
    frame?.ui.detailPanel.selectedNodeId === node.id ? frame.ui.detailPanel.tags : [];

  // The entity type already shows as the eyebrow above the title, so never repeat
  // it in the tag row (the agent sometimes includes the bare type in its tags).
  return source.filter((tag) => tag.toLowerCase() !== node.type.toLowerCase());
}
